/**
 * PermissionGate — Gap 1 graceful permission gating.
 *
 * Renders `children` only when the current actor holds the required
 * workspace permission. For non-delegates `hasWorkspacePermission` is always
 * true, so this is a transparent pass-through (no gating DOM). For delegates
 * lacking the permission it renders `fallback` (default: a denied card),
 * crucially WITHOUT mounting `children` — so the gated API calls those
 * children would make are never fired.
 */

import type { ReactNode } from 'react';
import {
  PermissionDeniedCard,
  type DelegationPermission,
} from '@/shared/components/sub-delegation';
import { useWorkspace } from '../context/WorkspaceProvider';

type PermissionGateProps = {
  permission: DelegationPermission;
  children: ReactNode;
  /** Replacement node when permission is missing. Defaults to a denied card. */
  fallback?: ReactNode;
  /** Optional feature name passed to the default denied card. */
  feature?: string;
  /** Variant for the default denied card. */
  variant?: 'page' | 'widget';
};

export function PermissionGate({
  permission,
  children,
  fallback,
  feature,
  variant = 'widget',
}: PermissionGateProps) {
  const { hasWorkspacePermission } = useWorkspace();

  if (hasWorkspacePermission(permission)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return <PermissionDeniedCard feature={feature} variant={variant} />;
}
